import { FC } from 'react';
import { Box, Theme, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { StyledLink, MenuBox } from '../style';
import whiteLogo from '../../../../media/whiteLogo.png';
import darkLogo from '../../../../media/blackLogo.png';
import '../logoStyles.css';
import MobileMenu from '../mobileMenu/MobileMenu';
import { useAppDispatch } from '../../../../hooks';
import useTranslations from '../../../global/translation/hooks/translation';
import { scrollFn } from '../helper';
import { useThemeContext } from '../../../global/ThemeContext';
import { setLanguage } from '../../../global/translation/translationSlice';
import DarkModeToggle from '../../DarkModeToggle';

const SecondAppBar: FC = () => {
  const { currentLanguage, translations } = useTranslations();
  const theme = useTheme();
  const { darkMode } = useThemeContext();
  const dispatch = useAppDispatch();

  // Place this component above SecondAppBar or in a separate file
  const MenuNavItem: FC<{ onClick: () => void; label: string; theme: Theme }> = ({ onClick, label, theme }) => (
    <Box
      sx={{
        cursor: 'pointer',
        mx: 1,
        color: darkMode ? '#FFF' : '#000',
        fontSize: 16,
        display: 'block',
        position: 'relative',
        transition: 'all 0.2s ease-in-out',
        fontFamily: '"Montserrat", "Segoe UI", "Arial", sans-serif',
        textShadow: darkMode ? '0 1px 4px rgba(255,255,255,0.08)' : '0 1px 4px rgba(0,0,0,0.08)',
        px: 1,
        py: 1,
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: 0,
          height: '2px',
          backgroundColor: darkMode ? '#FFF' : '#000',
          transition: 'width 0.2s ease-in-out',
        },
        '&:hover': {
          transform: 'scale(1.05)',
          textShadow: darkMode ? '0 2px 8px rgba(255,255,255,0.15)' : '0 2px 8px rgba(0,0,0,0.15)',
          '&::after': {
            width: '100%',
          },
        },
      }}
      onClick={onClick}
    >
      {label}
    </Box>
  );

  return (
    <>
      <Box
        sx={{
          px: { xs: 2, lg: 20 },
          background: darkMode ? '#181818' : theme.palette.background.default,
          color: darkMode ? '#FFF' : '#000',
          boxShadow: darkMode ? 'none' : '0px 2px 4px rgba(0, 0, 0, 0.1)',
        }}
      >
        <MobileMenu />
        <MenuBox
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          {/* Logo on the left */}
          <Box sx={{ flex: '0 0 auto', pr: 2 }}>
            <StyledLink to={'/'} onClick={() => scrollFn('top')}>
              <img
                style={{
                  scale: '0.7',
                }}
                alt="urban_equestrian"
                src={darkMode ? whiteLogo : darkLogo}
                className="whiteLogoStyles"
              />
            </StyledLink>
          </Box>
          {/* Menu items in the center */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, justifyContent: 'center' }}>
            {[
              { key: 'aboutUs', section: 'about', icon: <span style={{ marginRight: 4 }}></span> },
              { key: 'offer', section: 'offer', icon: <span style={{ marginRight: 4 }}></span> },
              { key: 'horses', section: 'horses', icon: <span style={{ marginRight: 4 }}></span> },
              { key: 'method', section: 'method', icon: <span style={{ marginRight: 4 }}></span> },
              { key: 'pricing', section: 'pricing', icon: <span style={{ marginRight: 4 }}></span> },
              { key: 'ourTeam', section: 'team', icon: <span style={{ marginRight: 4 }}></span> },
              { key: 'contact', section: 'contact', icon: <span style={{ marginRight: 4 }}></span> },
            ].map(({ key, section, icon }) => (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center' }}>
                {icon}
                <MenuNavItem onClick={() => scrollFn(section)} label={translations.menuItems[key][currentLanguage]} theme={theme} />
              </Box>
            ))}
          </Box>
          {/* Theme and language pickers on the right */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: '0 0 auto' }}>
            <DarkModeToggle />
            <Button
              onClick={() => {
                currentLanguage === 'en' ? dispatch(setLanguage('pl')) : dispatch(setLanguage('en'));
              }}
              size="large"
              sx={{ minWidth: 0, px: 2, ml: 1, fontWeight: 600, color: darkMode ? '#FFF' : '#000' }}
            >
              {currentLanguage === 'en' ? 'PL' : 'EN'}
            </Button>
          </Box>
        </MenuBox>
      </Box>
    </>
  );
};

export default SecondAppBar;
